<template>
  <div class="dashboard-page">
    <section class="hero-card">
      <div class="hero-main">
        <p class="hero-eyebrow">Opdrachtgeversportaal</p>
        <h1>Welkom, {{ opdrachtgeverStore.opdrachtgever?.contactnaam || 'Gebruiker' }}</h1>
        <p class="hero-subtitle">
          Kies hieronder waar u wilt starten. Alles is gegroepeerd per onderwerp en duidelijk uitgelegd.
        </p>
        <div class="hero-actions">
          <router-link to="/offertes" class="primary-action">
            Bekijk offertes
            <i class="pi pi-arrow-right"></i>
          </router-link>
          <p class="hero-note">Locaties volgen binnenkort.</p>
        </div>
      </div>
      <div class="hero-summary">
        <div class="summary-item">
          <span class="summary-value">{{ offerteStore.offertes.length }}</span>
          <span class="summary-label">Totaal offertes</span>
        </div>
        <div class="summary-item">
          <span class="summary-value">{{ pendingOffertes }}</span>
          <span class="summary-label">Openstaand</span>
        </div>
        <div class="summary-item">
          <span class="summary-value">{{ acceptedOffertes }}</span>
          <span class="summary-label">Geaccepteerd</span>
        </div>
      </div>
    </section>

    <section class="dashboard-section">
      <div class="section-header">
        <h2>Waar wilt u naartoe?</h2>
        <p>Kies een onderdeel. Niet alle onderdelen zijn al beschikbaar.</p>
      </div>

      <TransitionGroup name="dashboard-list" tag="div" class="cards-grid" appear>
        <router-link to="/offertes" class="nav-card" key="offertes" :style="{ '--delay': '0s' }">
          <div class="nav-card-header">
            <div class="nav-icon">
              <i class="pi pi-file"></i>
            </div>
            <div>
              <h3>Offertes</h3>
              <p>Bekijk, keur goed en volg de status.</p>
            </div>
          </div>
          <div class="nav-stats">
            <div class="stat-chip">
              <span class="stat-value">{{ pendingOffertes }}</span>
              <span class="stat-label">Openstaand</span>
            </div>
            <div class="stat-chip">
              <span class="stat-value">{{ acceptedOffertes }}</span>
              <span class="stat-label">Geaccepteerd</span>
            </div>
          </div>
          <div class="nav-cta">
            <span>Bekijk offertes</span>
            <i class="pi pi-arrow-right"></i>
          </div>
        </router-link>

        <router-link to="/rapporten" class="nav-card" key="rapporten" :style="{ '--delay': '0.1s' }">
          <div class="nav-card-header">
            <div class="nav-icon">
              <i class="pi pi-chart-bar"></i>
            </div>
            <div>
              <h3>Rapporten</h3>
              <p>Inzicht in inspectieresultaten.</p>
            </div>
          </div>
          <div class="nav-stats">
            <div class="stat-chip">
              <span class="stat-value">{{ openRapporten }}</span>
              <span class="stat-label">Lopend</span>
            </div>
            <div class="stat-chip">
              <span class="stat-value">{{ completedRapporten }}</span>
              <span class="stat-label">Afgerond</span>
            </div>
          </div>
          <div class="nav-cta">
            <span>Bekijk rapporten</span>
            <i class="pi pi-arrow-right"></i>
          </div>
        </router-link>

        <router-link to="/documenten" class="nav-card" key="documenten" :style="{ '--delay': '0.2s' }">
          <div class="nav-card-header">
            <div class="nav-icon">
              <i class="pi pi-folder-open"></i>
            </div>
            <div>
              <h3>Documenten</h3>
              <p>Alle certificaten, offertes en facturen op één plek.</p>
            </div>
          </div>
          <div class="nav-cta">
            <span>Bekijk documenten</span>
            <i class="pi pi-arrow-right"></i>
          </div>
        </router-link>

        <div class="nav-card is-disabled" key="locaties" :style="{ '--delay': '0.3s' }">
          <div class="nav-card-header">
            <div class="nav-icon">
              <i class="pi pi-map-marker"></i>
            </div>
            <div>
              <h3>Locaties</h3>
              <p>Beheer locaties en huurders.</p>
            </div>
          </div>
          <div class="coming-soon">Binnenkort beschikbaar</div>
        </div>
      </TransitionGroup>
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch, TransitionGroup } from 'vue'
  import type { OfferteStatus, Status } from '../../../../types'
  import { useOfferteStore } from '../stores/offertes'
  import { useAuthStore } from '../stores/auth'
  import { useRapportenStore } from '../stores/rapporten'
  import { useOpdrachtgeverStore } from '../stores/opdrachtgever'

  const offerteStore = useOfferteStore()
  const authStore = useAuthStore()
  const rapportenStore = useRapportenStore()
  const opdrachtgeverStore = useOpdrachtgeverStore()

  const hasLoaded = ref(false)

  const loadDashboardData = async (userId: string) => {
    if (hasLoaded.value) return
    hasLoaded.value = true

    try {
      console.debug('[dashboard] loading data', { userId })
      await Promise.all([
        opdrachtgeverStore.getOpdrachtgeverById(userId),
        offerteStore.fetchOffertes(),
        rapportenStore.fetchRapporten()
      ])
      console.debug('[dashboard] data loaded', {
        offertes: offerteStore.offertes.length,
        rapporten: rapportenStore.rapporten.length
      })
    } catch (error) {
      console.error('Error loading dashboard data:', error)
    }
  }

  // Load data when component mounts
  onMounted(async () => {
    try {
      await authStore.checkUser()
      if (authStore.user?.id) {
        await loadDashboardData(authStore.user.id)
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error)
    }
  })

  watch(
    () => authStore.user?.id,
    async (userId) => {
      if (userId) {
        await loadDashboardData(userId)
      }
    }
  )

  // Calculate statistics
  const closedStatuses: OfferteStatus[] = ['alles_geaccepteerd', 'ingepland', 'gefactureerd']

  const pendingOffertes = computed<number>(() => {
    let count = 0
    for (const offerte of offerteStore.offertes) {
      const statuses = (offerte.status ?? []) as Status[]
      const lastStatus = statuses[statuses.length - 1]?.status
      if (!lastStatus || !closedStatuses.includes(lastStatus)) {
        count += 1
      }
    }
    return count
  })

  const acceptedOffertes = computed<number>(() => {
    let count = 0
    for (const offerte of offerteStore.offertes) {
      const statuses = (offerte.status ?? []) as Status[]
      if (statuses.some(status => status.status === 'alles_geaccepteerd')) {
        count += 1
      }
    }
    return count
  })

  const openRapporten = computed<number>(() => {
    let count = 0
    for (const rapport of rapportenStore.rapporten) {
      const statuses = Array.isArray(rapport.inspectieStatus) ? rapport.inspectieStatus : []
      if (statuses.length === 0 || statuses.some(status => status.afgemeld !== true)) {
        count += 1
      }
    }
    return count
  })

  const completedRapporten = computed<number>(() => {
    let count = 0
    for (const rapport of rapportenStore.rapporten) {
      const statuses = Array.isArray(rapport.inspectieStatus) ? rapport.inspectieStatus : []
      if (statuses.length > 0 && statuses.every(status => status.afgemeld === true)) {
        count += 1
      }
    }
    return count
  })
</script>

<style scoped>
  .dashboard-page {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
    padding-bottom: 2rem;
  }

  .hero-card {
    background: linear-gradient(140deg, var(--p-background-0), var(--p-background-50));
    border-radius: var(--radius-sm);
    padding: 2.5rem;
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
    gap: 2rem;
    box-shadow: var(--shadow-lg);
  }

  .hero-main h1 {
    margin: 0 0 0.75rem;
    font-size: 2.25rem;
    color: var(--p-background-900);
  }

  .hero-eyebrow {
    margin: 0 0 0.5rem;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    font-weight: 600;
    color: var(--p-background-600);
  }

  .hero-subtitle {
    margin: 0 0 1.5rem;
    font-size: 1rem;
    line-height: 1.6;
    color: var(--p-background-700);
    max-width: 36rem;
  }

  .hero-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .primary-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.85rem 1.5rem;
    border-radius: var(--radius-md);
    background: var(--p-primary-500);
    color: var(--p-background-0);
    text-decoration: none;
    font-weight: 600;
    width: fit-content;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .primary-action:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 20px rgba(0, 0, 0, 0.12);
  }

  .hero-note {
    margin: 0;
    font-size: 0.9rem;
    color: var(--p-background-600);
  }

  .hero-summary {
    display: grid;
    gap: 1rem;
    align-content: start;
  }

  .summary-item {
    background: var(--p-background-0);
    border-radius: var(--radius-md);
    border: 1px solid var(--p-background-200);
    padding: 1rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .summary-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--p-background-900);
  }

  .summary-label {
    font-size: 0.9rem;
    color: var(--p-background-600);
  }

  .dashboard-section {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .section-header h2 {
    margin: 0 0 0.35rem;
    font-size: 1.5rem;
    color: var(--p-background-900);
  }

  .section-header p {
    margin: 0;
    color: var(--p-background-600);
  }

  .cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.5rem;
  }

  .nav-card {
    background: var(--p-background-0);
    border-radius: var(--radius-md);
    border: 1px solid var(--p-background-200);
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    text-decoration: none;
    color: inherit;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    box-shadow: var(--shadow-sm);
  }

  .nav-card:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-md);
  }

  .nav-card-header {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .nav-card-header h3 {
    margin: 0 0 0.25rem;
    font-size: 1.2rem;
    color: var(--p-background-900);
  }

  .nav-card-header p {
    margin: 0;
    color: var(--p-background-600);
    font-size: 0.95rem;
  }

  .nav-icon {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: var(--radius-md);
    background: var(--primary-alpha-10);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--p-primary-600);
    font-size: 1.25rem;
  }

  .nav-stats {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .stat-chip {
    flex: 1;
    min-width: 120px;
    background: var(--p-background-50);
    border-radius: var(--radius-md);
    padding: 0.75rem;
    border: 1px solid var(--p-background-200);
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .stat-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--p-background-900);
  }

  .stat-label {
    font-size: 0.85rem;
    color: var(--p-background-600);
  }

  .nav-cta {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--p-background-800);
    font-weight: 600;
  }

  .nav-card.is-disabled {
    background: var(--p-background-50);
    color: var(--p-background-500);
    box-shadow: none;
  }

  .nav-card.is-disabled .nav-icon {
    background: var(--p-background-100);
    color: var(--p-background-500);
  }

  .nav-card.is-disabled h3,
  .nav-card.is-disabled p {
    color: var(--p-background-600);
  }

  .coming-soon {
    text-align: left;
    color: var(--p-background-600);
    font-size: 0.9rem;
    font-weight: 600;
  }

  .dashboard-list-move,
  .dashboard-list-enter-active,
  .dashboard-list-leave-active,
  .dashboard-list-appear-active {
    transition: all 0.3s ease;
  }

  .dashboard-list-enter-from,
  .dashboard-list-appear-from {
    opacity: 0;
    transform: translateY(16px);
  }

  .dashboard-list-leave-to {
    opacity: 0;
    transform: translateY(-12px);
  }

  .dashboard-list-enter-active,
  .dashboard-list-appear-active {
    transition-delay: var(--delay);
  }

  .dashboard-list-leave-active {
    position: absolute;
  }

  .dashboard-list-move {
    transition: transform 0.3s ease;
  }

  @media (max-width: 960px) {
    .hero-card {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 640px) {
    .hero-card {
      padding: 1.75rem;
    }

    .hero-main h1 {
      font-size: 1.75rem;
    }

    .primary-action {
      width: 100%;
    }
  }
</style>
